import styles from "./ReasonToBuy.module.scss";
import { useRef, useState, useEffect } from "react";
import { UnderlinedButton } from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import classNames from "classnames/bind";
import { Card } from "./ReasonToBuy";
import Link from "next/link";
const cx = classNames.bind(styles);

// Matches SCSS breakpoints exactly:
// $from: md (600px) — tablet, 2 cards + peek
const MD_SCROLL_MEDIA_QUERY = "(min-width: 600px) and (max-width: 1023px)";
// $from: mdlg (1024px) — desktop
const LG_SCROLL_MEDIA_QUERY = "(min-width: 1024px)";

type Props = {
  cards: Card[];
};

export const ReasonToBuyUI = ({ cards }: Props) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const [activePage, setActivePage] = useState(0);
  const [cardsPerPage, setCardsPerPage] = useState(1);

  useEffect(
    function listenToMediaQueryChanges() {
      const mqMd = window.matchMedia(MD_SCROLL_MEDIA_QUERY);
      const mqLg = window.matchMedia(LG_SCROLL_MEDIA_QUERY);

      const update = () => {
        if (mqLg.matches) {
          // Desktop: scroll + pagination only when more than 6 cards,
          // otherwise all cards fill the row equally (totalPages === 1)
          setCardsPerPage(cards.length > 6 ? 4 : cards.length || 1);
        } else if (mqMd.matches) {
          setCardsPerPage(2);
        } else {
          setCardsPerPage(1);
        }
      };

      update();

      [mqMd, mqLg].forEach((mq) => {
        mq.addEventListener("change", update);
      });
      return () => {
        [mqMd, mqLg].forEach((mq) => {
          mq.removeEventListener("change", update);
        });
      };
    },
    // Re-run when cards.length changes so desktop cardsPerPage stays accurate
    [cards.length],
  );

  useEffect(
    function syncActivePageWithScroll() {
      const track = trackRef.current;
      if (!track) return;

      const handleScroll = () => {
        const items = Array.from(
          track.querySelectorAll<HTMLElement>("[data-card-index]"),
        );
        if (!items.length) return;

        const scrollLeft = track.scrollLeft;
        const scrollWidth = track.scrollWidth;
        const clientWidth = track.clientWidth;

        // Calculate which card is closest to the viewport start position
        // by checking actual DOM positions when available
        let closestIndex = 0;
        let closestDistance = Infinity;

        // Check if we have meaningful offsetLeft values by seeing if items have varying positions
        // In test environments, all items typically have offsetLeft: 0, so we detect that case
        const offsets = items
          .map((item) => item?.offsetLeft)
          .filter((offset): offset is number => typeof offset === "number");
        const hasValidOffsets =
          offsets.length > 0 &&
          (offsets.length === 1 || new Set(offsets).size > 1);

        if (hasValidOffsets) {
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item && typeof item.offsetLeft === "number") {
              const distance = Math.abs(item.offsetLeft - scrollLeft);
              if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = i;
              }
            }
          }
        } else if (scrollWidth > clientWidth) {
          // If offsetLeft values aren't available (e.g., in tests or SSR),
          // calculate proportionally based on scroll position
          const cardWidth = scrollWidth / items.length;
          closestIndex = Math.round(scrollLeft / cardWidth);
        }

        // Clamp to valid range to prevent out-of-bounds errors
        closestIndex = Math.max(0, Math.min(closestIndex, items.length - 1));

        setActivePage(Math.floor(closestIndex / cardsPerPage));
      };

      // Sync on mount and whenever cardsPerPage changes
      handleScroll();

      track.addEventListener("scroll", handleScroll, { passive: true });
      return () => track.removeEventListener("scroll", handleScroll);
    },
    [cards.length, cardsPerPage],
  );

  if (!cards.length) {
    return null;
  }

  const totalPages = Math.ceil(cards.length / cardsPerPage);

  return (
    <div data-component="ReasonToBuy" className={styles.container}>
      <ul
        className={styles.cards}
        data-testid="reason-to-buy-cards-track"
        ref={trackRef}
      >
        {cards.map((card, index) => (
          <li
            key={card.id}
            data-card-index={index}
            className={cx(styles.card, {
              "card--withOneView": totalPages === 1,
            })}
            data-testid={`reason-to-buy-card-${card.id}`}
          >
            <div className={styles.cardHeader}>
              {card.icon && (
                <span className={styles.cardIcon}>{card.icon}</span>
              )}
            </div>
            <p className={styles.cardTitle}>{card.title}</p>
            {card.description &&
              (card.descriptionHref ? (
                <a
                  href={card.descriptionHref}
                  className={styles.cardDescriptionLink}
                  data-testid={`reason-to-buy-card-${card.id}-link`}
                >
                  {card.description}
                </a>
              ) : card.onDescriptionClick ? (
                <UnderlinedButton
                  onClick={card.onDescriptionClick}
                  className={styles.cardDescriptionButton}
                  data-testid={`reason-to-buy-card-${card.id}-button`}
                  labelText={card.description}
                />
              ) : (
                <p className={styles.cardDescription}>{card.description}</p>
              ))}
            {card.linkUrl && card.linkText && (
              <Link
                className={styles.cardLinkButton}
                href={card.linkUrl}
                data-testid={`reason-to-buy-card-${card.id}-link`}
              >
                {card.linkText}
              </Link>
            )}
          </li>
        ))}
      </ul>
      {totalPages > 1 && (
        <div
          className={styles.pagination}
          aria-hidden="true"
          data-testid="reason-to-buy-pagination"
        >
          {Array.from({ length: totalPages }).map((_, pageIndex) => (
            <span
              key={`pagination-dot-${pageIndex}`}
              className={styles.paginationDot}
              data-active={pageIndex === activePage}
            />
          ))}
        </div>
      )}
    </div>
  );
};
